import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, ArrowUp, ArrowDown, Star, Loader2, X } from "lucide-react";
import { api, formatApiErrorDetail, resolveUrl } from "./api";
import { PageHeader, Card, Field, Input, Textarea, Toggle } from "./components/Field";
import { ImageUpload } from "./components/ImageUpload";
import { usePreview } from "./PreviewContext";

const blank = {
  tag: "",
  title: "",
  description: "",
  price: "",
  features: [],
  cta_label: "",
  cta_url: "",
  image_url: "",
  featured: false,
  wide: false,
};

const ProgramForm = ({ initial, onSave, onCancel, saving }) => {
  const [p, setP] = useState({ ...blank, ...initial });
  const update = (key, val) => setP((s) => ({ ...s, [key]: val }));
  const updateFeatures = (i, v) => update("features", p.features.map((x, idx) => (idx === i ? v : x)));
  const addFeature = () => update("features", [...(p.features || []), ""]);
  const removeFeature = (i) => update("features", p.features.filter((_, idx) => idx !== i));

  return (
    <div className="grid gap-5">
      <div className="grid sm:grid-cols-2 gap-5">
        <Field label="Tag (e.g. On-Ice)"><Input value={p.tag} onChange={(v) => update("tag", v)} /></Field>
        <Field label="Price"><Input value={p.price} onChange={(v) => update("price", v)} placeholder="$100 or Cost Varies" /></Field>
      </div>
      <Field label="Title"><Input value={p.title} onChange={(v) => update("title", v)} /></Field>
      <Field label="Description"><Textarea rows={4} value={p.description} onChange={(v) => update("description", v)} /></Field>

      <Field label="Feature pills (shown on featured card)">
        <div className="space-y-2">
          {(p.features || []).map((f, i) => (
            <div key={i} className="flex gap-2 items-center">
              <input value={f} onChange={(e) => updateFeatures(i, e.target.value)} className="flex-1 bg-transparent border-b-2 border-white/15 focus:border-brand-light outline-none py-2 text-white" />
              <button type="button" onClick={() => removeFeature(i)} className="text-zinc-500 hover:text-red-400 p-2"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
          <button type="button" onClick={addFeature} className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-brand-light hover:text-white">
            <Plus className="h-4 w-4" /> Add feature
          </button>
        </div>
      </Field>

      <div className="grid sm:grid-cols-2 gap-5">
        <Field label="CTA Label"><Input value={p.cta_label} onChange={(v) => update("cta_label", v)} placeholder="BOOK A CLINIC" /></Field>
        <Field label="CTA URL"><Input value={p.cta_url} onChange={(v) => update("cta_url", v)} /></Field>
      </div>

      <ImageUpload label="Card image" value={p.image_url} onChange={(v) => update("image_url", v)} />

      <div className="flex items-center justify-between gap-4 pt-2 border-t border-white/10">
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-6">
          <Toggle value={!!p.featured} onChange={(v) => update("featured", v)} label="Featured (large card)" />
          <Toggle value={!!p.wide} onChange={(v) => update("wide", v)} label="Wide (double-width)" />
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={onCancel} className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-400 hover:text-white px-3 py-2">Cancel</button>
          <button type="button" disabled={saving} onClick={() => onSave(p)} className="btn-primary !py-2.5 !px-5 !text-sm">
            {saving ? "Saving…" : "Save Program"}
          </button>
        </div>
      </div>
    </div>
  );
};

export const Programs = () => {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null); // null | "new" | program object
  const [saving, setSaving] = useState(false);
  const { reload: reloadPreview } = usePreview();

  const load = async () => {
    const { data } = await api.get("/cms/programs");
    setItems(data);
  };

  useEffect(() => { load(); }, []);

  const onSave = async (program) => {
    setSaving(true);
    try {
      if (editing === "new") {
        await api.post("/cms/programs", program);
        toast.success("Program created.");
      } else {
        await api.patch(`/cms/programs/${editing.id}`, program);
        toast.success("Program updated.");
      }
      setEditing(null);
      await load();
      reloadPreview();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this program? This cannot be undone.")) return;
    try {
      await api.delete(`/cms/programs/${id}`);
      toast.success("Deleted.");
      await load();
      reloadPreview();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Delete failed.");
    }
  };

  const move = async (idx, dir) => {
    const next = [...items];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    setItems(next);
    try {
      await api.post("/cms/programs/reorder", { ids: next.map((p) => p.id) });
      reloadPreview();
    } catch (err) {
      toast.error("Reorder failed.");
      load();
    }
  };

  if (items === null) return <div className="text-zinc-400 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;

  return (
    <>
      <PageHeader
        overline="Manage"
        title="Programs"
        description="Add, edit, reorder, or remove training programs. The featured one is shown as the large card on the Programs grid."
        actions={
          <button onClick={() => setEditing("new")} className="btn-primary !py-2.5 !px-5 !text-sm" data-testid="programs-new-button">
            <Plus className="h-4 w-4" /> New Program
          </button>
        }
      />

      {editing && (
        <Card title={editing === "new" ? "New Program" : `Edit · ${editing.title}`} description="">
          <ProgramForm
            initial={editing === "new" ? {} : editing}
            onSave={onSave}
            onCancel={() => setEditing(null)}
            saving={saving}
          />
        </Card>
      )}

      <div className="grid gap-3">
        {items.map((p, idx) => (
          <div key={p.id} className="bento-card p-4 flex items-center gap-4" data-testid={`admin-program-${idx}`}>
            <div className="h-16 w-20 bg-black/30 border border-white/10 overflow-hidden shrink-0">
              {p.image_url ? <img src={resolveUrl(p.image_url)} alt="" className="h-full w-full object-cover" /> : null}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[10px] tracking-[0.3em] uppercase font-bold text-brand-light">{p.tag}</span>
                {p.featured && (
                  <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-brand-light bg-brand/30 border border-brand/50 px-2 py-0.5 inline-flex items-center gap-1">
                    <Star className="h-3 w-3" /> Featured
                  </span>
                )}
              </div>
              <div className="font-display uppercase text-lg tracking-tight truncate">{p.title}</div>
              <div className="text-xs text-zinc-500 truncate mt-0.5">{p.price} · {p.cta_label}</div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button onClick={() => move(idx, -1)} className="p-2 text-zinc-500 hover:text-white" disabled={idx === 0}><ArrowUp className="h-4 w-4" /></button>
              <button onClick={() => move(idx, 1)} className="p-2 text-zinc-500 hover:text-white" disabled={idx === items.length - 1}><ArrowDown className="h-4 w-4" /></button>
              <button onClick={() => setEditing(p)} className="text-xs tracking-[0.2em] uppercase font-bold text-brand-light hover:text-white px-3 py-2">Edit</button>
              <button onClick={() => onDelete(p.id)} className="p-2 text-zinc-500 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="text-zinc-500 text-sm">No programs yet. Click "New Program" to add the first one.</div>}
      </div>
    </>
  );
};
